import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Card,
  Switch,
  Grid,
  Loader,
  Flex,
  Text,
  Image,
  Box,
  ActionIcon,
  Stack,
  ThemeIcon,
  Modal,
  UnstyledButton,
} from "@mantine/core";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { IconCaretUpFilled, IconCaretDownFilled } from "@tabler/icons-react";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

import { ActionBar, EmptyMessage } from "@components/shared";
import { AffiliateUserList } from "@components/Affiliate";

export default function AffiliateEntityJoins({ affiliateId }) {
  const queryClient = useQueryClient();

  const reqInfo = [{ key: "affiliate_id", value: affiliateId }]
    .filter((m) => m.value !== null)
    .reduce((acc, cur) => {
      acc[cur.key] = cur.value;
      return acc;
    }, {});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () =>
    queryClient.invalidateQueries(["affiliateEntityJoins", affiliateId]);

  // Fetch existing affiliates when adding is false
  const {
    data: affiliates,
    isLoading,
    isFetching,
  } = useQuery({
    initialData: [],
    queryKey: ["affiliateEntityJoins", affiliateId],
    queryFn: async () =>
      axios
        .post(`/affiliates/${affiliateId}/retrieve-joins/`, {
          ...reqInfo,
          page: 0,
          page_size: 100,
          search_value: "",
          classification: [1, 2],
          variety: [1, 2, 3],
        })
        .then(({ data }) => {
          return [
            ...data.response[0].locations.map((m) => ({
              ...m,
              location_id: m.id,
              affiliate_id: affiliateId,
            })),
            ...data.response[0].organizations.map((m) => ({
              ...m,
              organization_id: m.id,
              affiliate_id: affiliateId,
            })),
          ];
        })
        .catch((err) => {
          throw err;
        }),
  });

  const loading = isLoading || isFetching;

  const locations = affiliates.filter((affiliate) => affiliate.location_id);
  const organizations = affiliates.filter(
    (affiliate) => affiliate.organization_id
  );

  return (
    <div>
      {!loading && affiliates.length === 0 && (
        <Grid>
          <Grid.Col span={12}>
            <EmptyMessage
              title="No joins found"
              text="When this affiliate is added to locations and organizations, they will appear here."
            />
          </Grid.Col>
        </Grid>
      )}
      <Stack gap="xl">
        <EntityDisplay
          items={organizations}
          title="Organizations"
          color={entityColor.organization}
        >
          <ThemeIcon
            color={entityColor.organization}
            size="md"
            variant="light"
            radius="xl"
          >
            {entityIcon.organization(16)}
          </ThemeIcon>
        </EntityDisplay>
        <EntityDisplay
          items={locations}
          title="Locations"
          color={entityColor.location}
        >
          <ThemeIcon
            color={entityColor.location}
            size="md"
            variant="light"
            radius="xl"
          >
            {entityIcon.location(16)}
          </ThemeIcon>
        </EntityDisplay>
      </Stack>
    </div>
  );
}

const EntityDisplay = ({ title, items, children, color }) => {
  const [open, setOpen] = useState(true);
  if (items.length === 0) return null;

  return (
    <div>
      <Box mb="xs">
        <ActionBar color={color}>
          <div>{children}</div>
          <Text style={{ flexGrow: 1 }} fw={700}>
            {title} ({items.length})
          </Text>
          <ActionIcon
            size="sm"
            variant="subtle"
            color={color}
            radius="xl"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <IconCaretUpFilled size={16} />
            ) : (
              <IconCaretDownFilled size={16} />
            )}
          </ActionIcon>
        </ActionBar>
      </Box>
      {open && (
        <Grid>
          {items?.map((item) => (
            <Grid.Col span={{ base: 12, md: 4 }} key={item.id}>
              <Item entity={item} />
            </Grid.Col>
          ))}
        </Grid>
      )}
    </div>
  );
};

const Item = ({ entity, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  function onClose() {
    setIsOpen(false);
  }

  return (
    <Card>
      <Modal
        opened={isOpen}
        onClose={onClose}
        title={`${entity.name} Users`}
        size="fullscreen"
      >
        <AffiliateUserList
          reqData={{
            affiliate_id: entity.affiliate_id,
            location_id: entity.location_id,
            organization_id: entity.organization_id,
          }}
        />
      </Modal>
      <Flex gap="lg" align="flex-start">
        {entity.logo_url ? (
          <Image
            src={entity.logo_url}
            h={50}
            w={50}
            radius="xs"
            fit="contain"
          />
        ) : (
          <Box h={50} w={50} radius="xs" bg="gray.1"></Box>
        )}
        <div style={{ flexGrow: 1 }}>
          <Flex gap="xs" align="center">
            <Text
              fw={700}
              component={Link}
              to={
                entity.location_id
                  ? `/locations/${entity.location_id}/affiliates`
                  : `/organizations/${entity.organization_id}/affiliates`
              }
            >
              {entity.name}
            </Text>
          </Flex>
          {entity.city && entity.state_province ? (
            <Text c="dimmed" size="sm">
              {entity.city}, {entity.state_province}
            </Text>
          ) : null}
          <Text
            component={UnstyledButton}
            c="dimmed"
            size="sm"
            onClick={() => setIsOpen(true)}
          >
            View Users
          </Text>
          {/* <Box mt={5}>
            <Flex gap="xs" align="center">
              <ActionIcon
                size="sm"
                variant="light"
                color="gray"
                radius="xl"
                title="Open in new tab"
                component={Link}
                to={
                  entity.location_id
                    ? `/locations/${entity.location_id}/affiliates`
                    : `/organizations/${entity.organization_id}/affiliates`
                }
              >
                <IconExternalLink size={iconSize} />
              </ActionIcon>
              {children}
            </Flex>
          </Box> */}
        </div>
      </Flex>
    </Card>
  );
};
